import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNotification, NOTIFICATION_TYPES } from './NotificationContext';
import { getToken } from '../services/api';
import {
  setCrossDomainCookie,
  getCrossDomainCookie,
  removeCrossDomainCookie,
  CROSS_DOMAIN_KEYS
} from '../utils/crossDomainStorage';

// Use cross-domain cookie for sharing across subdomains
const ANALYSIS_COOKIE_KEY = CROSS_DOMAIN_KEYS.AUDIO_ANALYSIS_PENDING;

const AudioAnalysisContext = createContext(null);

export const useAudioAnalysis = () => {
  const context = useContext(AudioAnalysisContext);
  if (!context) {
    throw new Error('useAudioAnalysis must be used within AudioAnalysisProvider');
  }
  return context;
};

export const AudioAnalysisProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { addNotification } = useNotification();
  const [pendingAnalysis, setPendingAnalysis] = useState(null);
  const [completedAnalysis, setCompletedAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const xhrRef = useRef(null);
  const hasRestoredRef = useRef(false);

  // Load pending analysis from cross-domain cookie on mount (wait for auth to complete)
  useEffect(() => {
    // Don't run until auth loading is complete
    if (authLoading) return;
    // Only restore once
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const data = getCrossDomainCookie(ANALYSIS_COOKIE_KEY);
    if (data) {
      // Only restore if it's for the current user and still analyzing
      if (data.userId === user?.id && (data.status === 'analyzing' || data.status === 'uploading')) {
        setPendingAnalysis(data);
      } else if (user && data.userId !== user.id) {
        // Different user logged in, clear the old data
        removeCrossDomainCookie(ANALYSIS_COOKIE_KEY);
      }
      // Don't clear if user is not logged in - keep the data for when they log in
    }
  }, [authLoading, user]);

  // Save pending analysis to cross-domain cookie
  const savePendingAnalysis = useCallback((data) => {
    if (data) {
      setCrossDomainCookie(ANALYSIS_COOKIE_KEY, data, 1); // 1 day expiry
    } else {
      removeCrossDomainCookie(ANALYSIS_COOKIE_KEY);
    }
  }, []);

  // Start background analysis
  const startAnalysis = useCallback((file) => {
    if (!file || !user) return Promise.reject(new Error('No file or user'));

    // Clear previous states
    setCompletedAnalysis(null);
    setAnalysisError(null);
    setUploadProgress(0);

    const analysisData = {
      userId: user.id,
      fileName: file.name,
      fileSize: file.size,
      status: 'uploading',
      startedAt: new Date().toISOString()
    };

    setPendingAnalysis(analysisData);
    savePendingAnalysis(analysisData);

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('provider', 'local');

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      // Upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);

          if (percentComplete === 100) {
            const updatedData = { ...analysisData, status: 'analyzing' };
            setPendingAnalysis(updatedData);
            savePendingAnalysis(updatedData);
          }
        }
      });

      // Response handling
      xhr.addEventListener('load', () => {
        xhrRef.current = null;

        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success) {
              // Analysis complete
              setPendingAnalysis(null);
              savePendingAnalysis(null);
              setCompletedAnalysis(response.data);
              setUploadProgress(100);

              // Send notification
              if (user) {
                addNotification(
                  user.id,
                  NOTIFICATION_TYPES.AUDIO_ANALYSIS,
                  '음성 분석 완료',
                  `"${file.name}" 파일의 분석이 완료되었습니다.`,
                  '/ai/audio-analysis'
                );
              }

              resolve(response.data);
            } else {
              const error = response.error || '분석 중 오류가 발생했습니다.';
              setPendingAnalysis(null);
              savePendingAnalysis(null);
              setAnalysisError(error);
              reject(new Error(error));
            }
          } catch (e) {
            const error = '응답 처리 중 오류가 발생했습니다.';
            setPendingAnalysis(null);
            savePendingAnalysis(null);
            setAnalysisError(error);
            reject(new Error(error));
          }
        } else {
          let error = `서버 오류: ${xhr.status}`;
          try {
            const response = JSON.parse(xhr.responseText);
            error = response.error || error;
          } catch (e) {}

          setPendingAnalysis(null);
          savePendingAnalysis(null);
          setAnalysisError(error);
          reject(new Error(error));
        }
      });

      xhr.addEventListener('error', () => {
        xhrRef.current = null;
        const error = '네트워크 오류가 발생했습니다.';
        setPendingAnalysis(null);
        savePendingAnalysis(null);
        setAnalysisError(error);
        reject(new Error(error));
      });

      xhr.addEventListener('abort', () => {
        xhrRef.current = null;
        setPendingAnalysis(null);
        savePendingAnalysis(null);
      });

      xhr.open('POST', '/api/audio/analyze');

      const token = getToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  }, [user, addNotification, savePendingAnalysis]);

  // Cancel ongoing analysis
  const cancelAnalysis = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
    setPendingAnalysis(null);
    savePendingAnalysis(null);
    setUploadProgress(0);
    setAnalysisError(null);
  }, [savePendingAnalysis]);

  // Clear completed analysis
  const clearCompletedAnalysis = useCallback(() => {
    setCompletedAnalysis(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setAnalysisError(null);
  }, []);

  // Check if analysis is in progress
  const isAnalyzing = Boolean(pendingAnalysis);

  const value = {
    pendingAnalysis,
    completedAnalysis,
    analysisError,
    uploadProgress,
    isAnalyzing,
    startAnalysis,
    cancelAnalysis,
    clearCompletedAnalysis,
    clearError
  };

  return (
    <AudioAnalysisContext.Provider value={value}>
      {children}
    </AudioAnalysisContext.Provider>
  );
};
